// src/pages/Subscriptions.js
// Updated to reflect current pricing model:
// - Drivers: always free, no subscriptions
// - Hirers: Basic (free, 3 posts/month) or Pro ($159/mo | $1,699/yr CAD)
// - Add-ons: Profile Boost, Priority Applicant (drivers V2), Featured Job Post (hirers)
import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import Paginator from "../components/Paginator";

const PAGE_SIZE = 25;

const HIRER_PLAN_DISPLAY = {
  basic:       { label: "Basic",      color: "var(--text-secondary)", bg: "rgba(136,136,136,0.12)" },
  pro_monthly: { label: "⭐ Pro Monthly", color: "#F97316", bg: "#F9731620" },
  pro_yearly:  { label: "⭐ Pro Yearly",  color: "#F97316", bg: "#F9731620" },
};

const fmtCAD = (n) => `$${Number(n).toFixed(2)} CAD`;

export default function Subscriptions() {
  const [users,      setUsers]      = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [roleFilter, setRoleFilter] = useState("hirer");
  const [planFilter, setPlanFilter] = useState("all");
  const [search,     setSearch]     = useState("");
  const [stats,      setStats]      = useState({});
  const [page,       setPage]       = useState(1);
  const [changing,   setChanging]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(data);

      const drivers = data.filter(u => u.role === "driver");
      const hirers  = data.filter(u => u.role === "hirer");

      const hirerProMonthly = hirers.filter(h => h.planId === "pro_monthly" && h.subscriptionActive).length;
      const hirerProYearly  = hirers.filter(h => h.planId === "pro_yearly"  && h.subscriptionActive).length;
      const hirerBasic      = hirers.filter(h => !h.subscriptionActive || h.planId === "basic" || !h.planId).length;

      // Estimated monthly recurring revenue (CAD)
      const mrr = (hirerProMonthly * 159) + (hirerProYearly * (1699 / 12));

      setStats({
        totalDrivers:     drivers.length,
        totalHirers:      hirers.length,
        hirerBasic,
        hirerProMonthly,
        hirerProYearly,
        hirerProTotal:    hirerProMonthly + hirerProYearly,
        mrr:              mrr.toFixed(2),
        arr:              (mrr * 12).toFixed(2),
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let result = users.filter(u => u.role === roleFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(u =>
        u.fullName?.toLowerCase().includes(s) ||
        u.email?.toLowerCase().includes(s)
      );
    }
    if (planFilter === "pro")   result = result.filter(u => u.subscriptionActive);
    if (planFilter === "basic") result = result.filter(u => !u.subscriptionActive || u.planId === "basic" || !u.planId);
    setFiltered(result);
    setPage(1);
  }, [search, planFilter, roleFilter, users]);

  const handleChangePlan = async (user, newPlan) => {
    const isPro = newPlan === "pro_monthly" || newPlan === "pro_yearly";
    setChanging(user.id);
    try {
      await updateDoc(doc(db, "users", user.id), {
        planId:             newPlan,
        subscriptionActive: isPro,
        updatedAt:          serverTimestamp(),
      });
      load();
    } catch (e) { alert("Error updating plan."); }
    finally { setChanging(null); }
  };

  const inp = {
    background: "var(--surface2)", border: "1px solid var(--border)",
    borderRadius: 8, padding: "9px 14px", color: "var(--text)",
    fontSize: 13, outline: "none",
  };

  return (
    <div className="animate-fade">

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "Syne", fontSize: 28, fontWeight: 800, color: "var(--text)", margin: 0 }}>
          Subscriptions
        </h1>
        <p style={{ color: "var(--text-muted)", marginTop: 4, fontSize: 14 }}>
          Hirer plan management and revenue tracking · Drivers are always free
        </p>
      </div>

      {/* Revenue + Stats cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
        {[
          { label: "Est. MRR (CAD)",     value: `$${stats.mrr || "0.00"}`,  icon: "💰", color: "#F97316", sub: "Monthly Recurring Revenue" },
          { label: "Hirer Pro",          value: stats.hirerProTotal || 0,   icon: "⭐", color: "#F97316", sub: `${stats.hirerProMonthly || 0} monthly · ${stats.hirerProYearly || 0} yearly` },
          { label: "Hirer Basic",        value: stats.hirerBasic || 0,      icon: "🏢", color: "#3B82F6", sub: "Free tier" },
          { label: "Total Drivers",      value: stats.totalDrivers || 0,    icon: "🚛", color: "#22C55E", sub: "Always free" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--surface)", border: `1px solid ${s.color}22`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color, fontFamily: "Syne" }}>{s.value}</div>
            <div style={{ color: "var(--text)", fontSize: 13, fontWeight: 600, marginTop: 4 }}>{s.label}</div>
            <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Plan breakdown cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 }}>

        {/* Drivers */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--surface2)", borderRadius: 14, padding: 20 }}>
          <h3 style={{ fontFamily: "Syne", color: "#22C55E", marginTop: 0, marginBottom: 16 }}>🚛 Driver Pricing — V1</h3>
          {[
            ["Free Forever", "$0", "Unlimited job applications, verified CDL profile, messaging, reviews"],
          ].map(([n, p, d]) => (
            <div key={n} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid var(--surface2)" }}>
              <div>
                <div style={{ color: "var(--text)", fontWeight: 600, fontSize: 13 }}>{n}</div>
                <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>{d}</div>
              </div>
              <div style={{ color: "#22C55E", fontWeight: 800, flexShrink: 0, marginLeft: 12 }}>{p}</div>
            </div>
          ))}
          <div style={{ marginTop: 14, background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 8, padding: "10px 14px" }}>
            <div style={{ color: "#22C55E", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>🚀 V2 Add-ons (Coming Soon)</div>
            {[
              ["Profile Boost", "$7.99 – $19.99", "7 / 14 / 30 days — float above others in search"],
              ["Priority Applicant", "$5.99 – $14.99", "7 / 14 / 30 days — appear higher in applicant lists"],
            ].map(([n, p, d]) => (
              <div key={n} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(34,197,94,0.1)" }}>
                <div>
                  <div style={{ color: "var(--text)", fontSize: 12, fontWeight: 600 }}>{n}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: 11 }}>{d}</div>
                </div>
                <div style={{ color: "#22C55E", fontWeight: 700, fontSize: 12, flexShrink: 0, marginLeft: 8 }}>{p}</div>
              </div>
            ))}
            <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 8 }}>Purchased via crewboard.ca — no Apple/Google cut</div>
          </div>
        </div>

        {/* Hirers */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--surface2)", borderRadius: 14, padding: 20 }}>
          <h3 style={{ fontFamily: "Syne", color: "#F97316", marginTop: 0, marginBottom: 16 }}>🏢 Hirer Pricing</h3>
          {[
            ["Basic",      "$0",           "3 posts/month, browse profiles, receive applications, messaging with applicants only"],
            ["Pro Monthly","$159 / mo CAD","Unlimited posts, Search Drivers, advanced filters, saved pools, priority support"],
            ["Pro Yearly", "$1,699 / yr CAD","Everything in Pro Monthly — save 11% (~$141/mo)"],
          ].map(([n, p, d]) => (
            <div key={n} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid var(--surface2)" }}>
              <div>
                <div style={{ color: "var(--text)", fontWeight: 600, fontSize: 13 }}>{n}</div>
                <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>{d}</div>
              </div>
              <div style={{ color: "#F97316", fontWeight: 800, flexShrink: 0, marginLeft: 12, textAlign: "right" }}>{p}</div>
            </div>
          ))}
          <div style={{ marginTop: 14, background: "rgba(249,115,22,0.06)", border: "1px solid rgba(249,115,22,0.2)", borderRadius: 8, padding: "10px 14px" }}>
            <div style={{ color: "#F97316", fontSize: 12, fontWeight: 700, marginBottom: 6 }}>📌 Add-on (Any Paid Plan)</div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: "var(--text)", fontSize: 12, fontWeight: 600 }}>Featured Job Post</div>
                <div style={{ color: "var(--text-muted)", fontSize: 11 }}>Pin job to top of listings for 7 days</div>
              </div>
              <div style={{ color: "#F97316", fontWeight: 700, fontSize: 12 }}>$19.99 CAD</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search name or email..."
          style={{ ...inp, flex: 1, minWidth: 200 }}
        />
        <div style={{ display: "flex", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
          {[["hirer", "🏢 Hirers"], ["driver", "🚛 Drivers"]].map(([r, label]) => (
            <button key={r} onClick={() => { setRoleFilter(r); setPlanFilter("all"); }}
              style={{ background: roleFilter === r ? "#F97316" : "transparent", border: "none", color: roleFilter === r ? "#fff" : "var(--text-secondary)", padding: "9px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {label}
            </button>
          ))}
        </div>
        {roleFilter === "hirer" && ["all", "pro", "basic"].map(f => (
          <button key={f} onClick={() => setPlanFilter(f)}
            style={{ background: planFilter === f ? "#F97316" : "var(--surface)", border: `1px solid ${planFilter === f ? "#F97316" : "var(--border)"}`, color: planFilter === f ? "#fff" : "var(--text-secondary)", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {f === "all" ? "All" : f === "pro" ? "⭐ Pro" : "Basic"}
          </button>
        ))}
      </div>

      {/* Users table */}
      {loading ? (
        <div style={{ color: "var(--text-muted)", textAlign: "center", padding: 60 }}>Loading...</div>
      ) : (() => {
        const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
        const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
        return (
          <>
            <div style={{ background: "var(--surface)", border: "1px solid var(--surface2)", borderRadius: 16, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1.5fr 1.2fr 1fr", gap: 16, padding: "12px 20px", borderBottom: "1px solid var(--surface2)", color: "var(--text-muted)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>
                <span>Name</span><span>Email</span><span>Plan</span><span>Status</span>
                <span>{roleFilter === "hirer" ? "Change Plan" : "—"}</span>
              </div>

              {paginated.length === 0 ? (
                <div style={{ color: "var(--text-muted)", textAlign: "center", padding: 40 }}>No users found</div>
              ) : paginated.map((user, i) => {
                const isHirer = user.role === "hirer";
                const planKey = user.planId || (isHirer ? "basic" : "free");
                const plan    = HIRER_PLAN_DISPLAY[planKey] || HIRER_PLAN_DISPLAY.basic;

                return (
                  <div key={user.id} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1.5fr 1.2fr 1fr", gap: 16, padding: "14px 20px", borderBottom: i < paginated.length - 1 ? "1px solid var(--surface2)" : "none", alignItems: "center" }}>
                    <div style={{ color: "var(--text)", fontWeight: 600, fontSize: 14 }}>{user.fullName || "—"}</div>
                    <div style={{ color: "var(--text-secondary)", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
                    <div>
                      {isHirer ? (
                        <span style={{ background: plan.bg, color: plan.color, borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                          {plan.label}
                        </span>
                      ) : (
                        <span style={{ background: "rgba(34,197,94,0.1)", color: "#22C55E", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                          🚛 Free Forever
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13 }}>
                      {isHirer ? (
                        user.subscriptionActive
                          ? <span style={{ color: "#22C55E" }}>✅ Active</span>
                          : <span style={{ color: "var(--text-muted)" }}>⭕ Free</span>
                      ) : (
                        <span style={{ color: "#22C55E" }}>✅ Active</span>
                      )}
                    </div>
                    <div>
                      {isHirer ? (
                        <select
                          onChange={e => handleChangePlan(user, e.target.value)}
                          value={user.planId || "basic"}
                          disabled={changing === user.id}
                          style={{ ...inp, padding: "5px 10px", fontSize: 12, opacity: changing === user.id ? 0.5 : 1 }}
                        >
                          <option value="basic">Basic (Free)</option>
                          <option value="pro_monthly">Pro Monthly</option>
                          <option value="pro_yearly">Pro Yearly</option>
                        </select>
                      ) : (
                        <span style={{ color: "var(--text-muted)", fontSize: 12 }}>N/A</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {filtered.length > PAGE_SIZE && (
              <Paginator page={page} totalPages={totalPages} totalItems={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
            )}
          </>
        );
      })()}

      {/* Pricing notes */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--surface2)", borderRadius: 14, padding: 20, marginTop: 24 }}>
        <h3 style={{ fontFamily: "Syne", color: "var(--text)", marginTop: 0, marginBottom: 12 }}>💡 Pricing Notes</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            ["Drivers are free forever", "Revenue comes from hirers only. No driver subscriptions in V1."],
            ["Hirer payments via web only", "app.crewboard.ca — avoids Apple 30% / Google 15-30% cut."],
            ["Driver add-ons via crewboard.ca", "Profile Boost, Priority Applicant — purchased on website in V2."],
            ["FREE_LAUNCH_MODE = true", "All features unlocked. Billing enforced when Stripe goes live."],
            ["Trial rollout plan", "Generate offer codes for all existing hirers when billing goes live."],
            ["Offer codes", "Manage in the Offer Codes page — free_period, percent_off, fixed_off."],
          ].map(([title, desc]) => (
            <div key={title} style={{ background: "var(--surface2)", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ color: "var(--text)", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{title}</div>
              <div style={{ color: "var(--text-muted)", fontSize: 12, lineHeight: 1.5 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}